import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import BarChartIcon from '@mui/icons-material/PollOutlined';
import { Skeleton, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Highcharts from 'highcharts';
import { DateTime } from 'luxon';
import { Fragment, useEffect, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Loadable } from 'recoil';
import {
  useGetUsageReportByBusinessIdQuery,
  useSuperAdminBusinessStatsQuery,
} from '../../../../../api/apigQuery';
import { HighchartsReactWithStyles } from '../../../../../app/components/charts/HighchartsReactWithStyles';
import { toggleSuperAdminDrawer } from '../../../../../app/store/superAdminStatsSlice';
import { PopoverGeneric } from '../../../../../common/PopoverGeneric';
import { IUserAtom } from '../../../../../recoil/atoms/user';
import {
  getIsDeveloper,
  getUserSettings,
} from '../../../../../recoil/selectors/permissionsSelector';

interface ICRMStats {
  crmid: number;
  collectedAt: string;
  contactsCount: number;
  usersCount: number;
  esContactsCount: number | undefined;
}

const useStyles = makeStyles((theme) => ({
  noDataContainer: {},
  barSkeleton: {},
  ontrial: {
    color: 'red',
  },
  paid: {
    color: 'green',
  },
  statsContainer: {
    margin: '0 auto',
    width: '100%',
  },
  adminBolded: {
    fontWeight: 'bold',
  },
  statsFlexContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'start',
    gap: '10px',
    margin: '0 0',
  },
  statListItem: {
    listStyleType: 'none',
    padding: '0px',
    margin: 0,
  },
  bidStatsToggle: {
    background: 'none !important',
    content: '""',
    display: 'block !important',
    borderLeft: '20px solid transparent !important',
    borderRight: '20px solid transparent !important',
    borderBottom: '0 solid transparent !important',
    borderTop: '30px solid #ffec61 !important',
    width: '100px',
    height: 0,
    position: 'absolute',
    left: '47%',
    '&:after': {
      display: 'none !important',
    },
  },
  bidStatsToggleIcon: {
    marginTop: -70,
    color: '#000 !important',
    paddingLeft: 9,
    '.show &': {
      transform: 'scale(1, -1) !important',
    },
  },
  statsInner: {
    background: '#fff7c1',
    float: 'none',
    position: 'relative !important' as 'relative',
    transform: 'none !important',
    padding: '.5rem 1.25rem',
    boxShadow: 'none',
    opacity: '1 !important',
    '.bid-stats-open &': {
      display: 'flex !important',
      alignItems: 'center',
      //minHeight: '180px',
    },
  },
  statItemContainer: {
    //overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '100px',
    textAlign: 'center',
  },
  statGreaterOrEqual: {
    color: 'darkgreen',
    fontWeight: 600,
  },
  statLesser: {
    color: 'darkred',
    fontWeight: 600,
  },
}));

const SuperAdminStats = ({ userAtom }: { userAtom: Loadable<IUserAtom> }) => {
  const classes = useStyles();
  if ('businessid' in userAtom.contents) {
    userAtom.contents.userSettings;
  }

  const { data: usageReportData } = useGetUsageReportByBusinessIdQuery(
    {
      userSettings: {
        businessid: (userAtom.contents as IUserAtom).businessid,
        businessId: (userAtom.contents as IUserAtom).businessid,
        userId: (userAtom.contents as IUserAtom).userid,
        userid: (userAtom.contents as IUserAtom).userid,
        apikey: (userAtom.contents as IUserAtom).apikey,
      },
      size: 20,
    },
    {
      skip: !userAtom.contents || !('businessid' in userAtom.contents),
    }
  );

  const showUsageReport = usageReportData?.length! > 0;
  const usageReprotSeriesData = useMemo<
    [date: number, usedRequests: number][]
  >(() => {
    return (
      usageReportData?.map((report) => [
        Date.parse(report?._source?.createdDate!),
        report?._source?.usedRequests,
      ]) ?? []
    );
  }, [usageReportData]);
  const apiLevelSeriesData = useMemo<
    [date: number, quotaLimit: number][]
  >(() => {
    return (
      usageReportData?.map((report) => [
        Date.parse(report?._source?.createdDate!),
        report?._source?.quotaLimit,
      ]) ?? []
    );
  }, [usageReportData]);

  const usageReportChartOptions: Highcharts.Options = {
    title: {
      text: 'Usage Chart Report',
    },
    subtitle: {
      text: 'Last 20 data points where usage was above zero',
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        // don't display the year
        month: '%e. %b',
        year: '%b',
      },
      labels: {
        formatter: function () {
          return new Date(this.value).toLocaleDateString('en-us', {
            timeZone: 'UTC',
            month: 'numeric',
            day: 'numeric',
          });
          // return Highcharts.dateFormat('%m/%e', this.value);
        },
      },
      title: {
        text: 'Date',
      },
    },
    yAxis: [
      {
        title: {
          text: 'API Call Count',
          style: { color: '#6CF' },
        },
        min: 0,
        labels: {
          style: { color: '#6CF' },
        },
      },
      {
        title: {
          text: 'API Level',
          style: { color: 'red' },
        },
        min: 0,
        labels: {
          style: { color: 'red' },
        },
        opposite: true,
      },
    ],
    tooltip: {
      headerFormat: '{point.x:%b %e, %Y}<br/>',
      pointFormat: '{series.name}: <b>{point.y:.0f}</b>',
    },
    plotOptions: {
      series: {
        marker: {
          enabled: true,
          radius: 2.5,
        },
      },
    },
    colors: ['#6CF', 'red'],
    series: [
      {
        type: 'column',
        name: 'API Call Count',
        yAxis: 0,
        data: usageReprotSeriesData,
      },
      {
        type: 'line',
        name: 'API Level',
        yAxis: 1,
        data: apiLevelSeriesData,
      },
    ],
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            minWidth: 500,
          },
        },
      ],
    },
  };

  const {
    data: superAdminBusinessStatsData,
    isSuccess: superAdminBusinessStatsSuccess,
    isLoading: superAdminBusinessStatsLoading,
    refetch: refetchToggleForSuperAdminBusinessStats,
  } = useSuperAdminBusinessStatsQuery(
    {
      userSettings: {
        businessid: (userAtom.contents as IUserAtom).businessid,
        businessId: (userAtom.contents as IUserAtom).businessid,
        userId: (userAtom.contents as IUserAtom).userid,
        userid: (userAtom.contents as IUserAtom).userid,
        apikey: (userAtom.contents as IUserAtom).apikey,
      },
    },
    {
      skip: !userAtom.contents || !('businessid' in userAtom.contents),
    }
  );

  const adminBusinessStats = useMemo(() => {
    if (superAdminBusinessStatsSuccess) {
      superAdminBusinessStatsData.data;

      let parsedStats;
      try {
        parsedStats = JSON.parse(superAdminBusinessStatsData.data.JSON);
      } catch (er) {
        console.error(er);
      }

      const displayDate = new Date();
      const createdDate = displayDate.toISOString().split('T')[0];
      const formatMoney = (amount: number) => {
        if (!amount) {
          return '$0';
        }
        const newAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(amount);
        return newAmount.slice(0, -3); //remove the last three chars
      };
      var isUsageDataForToday =
        superAdminBusinessStatsData.data?.apiUsage?.createdDate === createdDate;

      return {
        businessId: String(parsedStats?.businessid) ?? '0',
        hasCustomCRM: parsedStats?.hasCustomCRM ?? '0',
        invoicedUntil: parsedStats?.invoicedUntil ?? '0',
        lastinvoicedate: parsedStats?.lastinvoicedate ?? '0',
        minimumBalance: formatMoney(parsedStats?.minimumBalance) ?? '0',
        billDay: parsedStats?.billDay ?? '0',
        discountPercent: parsedStats?.discountPercent ?? '0',
        lastcharge: parsedStats?.lastcharge ?? '0',
        reserveBalance: formatMoney(parsedStats?.reserveBalance) ?? '0',
        rechargeAmount: formatMoney(parsedStats?.rechargeAmount) ?? '0',
        fourDayRechargeCount: parsedStats?.fourDayCount ?? '0',
        fourDayRechargeAmount: formatMoney(parsedStats?.fourDayAmount) ?? '0',
        totalProductSpend: formatMoney(parsedStats?.totalProductSpend) ?? '0',
        EndingMRR: formatMoney(parsedStats?.EndingMRR) ?? '0',
        StripeCustomerID: parsedStats?.cf_StripeCustomerID ?? '0',
        revPerActiveUser: formatMoney(parsedStats?.revPerActiveUser) ?? '0',
        MaxMRR: formatMoney(parsedStats?.MaxMRR) ?? '0',
        MinMRR: formatMoney(parsedStats?.MinMRR) ?? '0',
        AvgMRR: formatMoney(parsedStats?.AvgMRR) ?? '0',
        MaxChange: formatMoney(parsedStats?.MaxChange) ?? '0',
        MinChange: formatMoney(parsedStats?.MinChange) ?? '0',
        AvgChange: formatMoney(parsedStats?.AvgChange) ?? '0',
        PaidMonthsCt: parsedStats?.PaidMonthsCt ?? '0',
        prepaidTrial: parsedStats?.prepaidTrial ?? '0',
        billday: String(parsedStats?.billDay) ?? '0',
        smstcount: parsedStats?.smstcount ?? '0',
        businessSMScount: parsedStats?.businessSMScount ?? '0',
        vmdcount: parsedStats?.vmdcount ?? '0',
        businessBillingTerm: parsedStats?.billingTerm ?? '0',
        businessContractTerm: parsedStats?.contractTerm ?? '0',
        name: String(parsedStats?.name) ?? 'no name',
        timezone: String(parsedStats?.businessTimezone) ?? 'no tz',
        activeAgents: parsedStats?.numActiveUsers ?? '0',
        numbers: String(parsedStats?.numbercount) ?? '0',
        unallocatedNumbers: String(parsedStats?.UnallocatedNumbers) ?? '0',
        businessSMSnumbers: String(parsedStats?.businessSMSnumbers) ?? '0',
        NonSMS: parsedStats?.NonSMS ?? '0',
        NonSMSPct:
          ((parsedStats?.NonSMS / parsedStats?.numbercount) * 100).toFixed(0) ??
          0,
        ImageSpamCount: parsedStats?.ImageSpamCount ?? 0,
        ImageSpamPct:
          (
            (parsedStats?.ImageSpamCount / parsedStats?.numbercount) *
            100
          ).toFixed(0) ?? 0,
        blockedNumbers:
          String(superAdminBusinessStatsData.data?.blocked) ?? '0',
        ringGroups: parsedStats?.groupcount ?? '0',
        teams: parsedStats?.teamscount ?? '0',
        queues: parsedStats?.queuescount ?? '0',
        ivrs: parsedStats?.ivrcount ?? '0',
        conferences: String(parsedStats?.conference) ?? '0',
        dispositions: String(parsedStats?.disposition) ?? '0',
        cadences: parsedStats?.cadencecount ?? '0',
        powerlists: parsedStats?.powerlistcount ?? '0',
        webhooks: parsedStats?.webhookcount ?? '0',
        contacts: String(superAdminBusinessStatsData.data?.contact) ?? '0',
        basicphone: parsedStats?.basicphone ?? '0',
        basicphoneunlimited: parsedStats?.basicphoneunlimited ?? '0',
        enterprisephone: parsedStats?.enterprisephone ?? '0',
        enterprisephoneunlimited: parsedStats?.enterprisephoneunlimited ?? '0',
        integratedphone: parsedStats?.integratedphone ?? '0',
        integratedphoneunlimited: parsedStats?.integratedphoneunlimited ?? '0',
        payperuse: parsedStats?.freeAgents ?? '0',
        localpresencelite: parsedStats?.localpresencelite ?? '0',
        ftcblocking: parsedStats?.ftcblocking ?? '0',
        connectionboostprivate: parsedStats?.connectionboostprivate ?? '0',
        callTranscription: parsedStats?.callTranscription ?? '0',
        machineDetection: parsedStats?.machineDetection ?? '0',
        powerdialer1: parsedStats?.powerdialer1 ?? '0',
        powerdialer4: parsedStats?.powerdialer4 ?? '0',
        powerdialer10: parsedStats?.powerdialer10 ?? '0',
        totalPhones: parsedStats?.totalPhones ?? '0',
        OBWStatus: parsedStats?.OBWStatus ?? '0',
        dispositionCount: parsedStats?.dispositionCount ?? '0',
        externalDispositionCount: parsedStats?.externalDispositionCount ?? '0',
        dispositionRulesCount: parsedStats?.dispositionRulesCount ?? '0',
        agentStatAvgOutgoingPerAgent:
          parsedStats?.AvgOutgoingPerAgent?.toFixed(1) ?? 0,
        agentStatAvgOutgoingAnsweredPerAgent:
          parsedStats?.AvgOutgoingAnsweredPerAgent?.toFixed(1) ?? 0,
        agentStatAvgOutgoingCountOver150PerAgent:
          parsedStats?.AvgOutgoingCountOver150PerAgent?.toFixed(1) ?? 0,
        agentStatOutgoingConnectionRateOver150PerAgent:
          parsedStats?.OutgoingConnectionRateOver150PerAgent?.toFixed(0) ?? 0,
        agentStatOutgoingConnectionUnder30PerAgent:
          parsedStats?.OutgoingConnectionUnder30PerAgent?.toFixed(1) ?? 0,
        agentStatAvgIncomingPerAgent:
          parsedStats?.AvgIncomingPerAgent?.toFixed(1) ?? 0,
        agentStatIncomingMissedRatePerAgent:
          parsedStats?.IncomingMissedRatePerAgent?.toFixed(0) ?? 0,
        agentStatAvgOutgoingPerAgentSMS:
          parsedStats?.AvgOutgoingPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgOutgoingMMSPerAgentSMS:
          parsedStats?.AvgOutgoingMMSPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgOutgoingBlockedPerAgentSMS:
          parsedStats?.AvgOutgoingBlockedPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgOutgoingBusinessSMSPerAgentSMS:
          parsedStats?.AvgOutgoingBusinessSMSPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgIncomingPerAgentSMS:
          parsedStats?.AvgIncomingPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgIncomingMMSPerAgentSMS:
          parsedStats?.AvgIncomingMMSPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgIncomingBlockedPerAgentSMS:
          parsedStats?.AvgIncomingBlockedPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgIncomingBusinessSMSPerAgentSMS:
          parsedStats?.AvgIncomingBusinessSMSPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgTotalPerAgentSMS:
          parsedStats?.AvgTotalPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgTotalMMSPerAgentSMS:
          parsedStats?.AvgTotalMMSPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgTotalBlockedPerAgentSMS:
          parsedStats?.AvgTotalBlockedPerAgentSMS?.toFixed(0) ?? 0,
        agentStatAvgTotalBusinessSMSPerAgentSMS:
          parsedStats?.AvgTotalBusinessSMSPerAgentSMS?.toFixed(0) ?? 0,
        crmStats: Array.from<ICRMStats>(
          superAdminBusinessStatsData.data?.crmStats ?? []
        ),
        apiUsedRequests:
          isUsageDataForToday === false
            ? '0'
            : (superAdminBusinessStatsData.data?.apiUsage?.usedRequests > 0
                ? superAdminBusinessStatsData.data?.apiUsage?.usedRequests
                : '0') +
              (superAdminBusinessStatsData.data?.apiUsage?.quotaLimit > 0
                ? '/' + superAdminBusinessStatsData.data?.apiUsage?.quotaLimit
                : '') +
              (superAdminBusinessStatsData.data?.apiUsage?.percentageUsed > 0
                ? ' (' +
                  (
                    superAdminBusinessStatsData.data?.apiUsage?.percentageUsed *
                    100
                  ).toFixed(3) +
                  '%)'
                : ''),
        AE: parsedStats?.AssignedAE ?? '',
        BDR: parsedStats?.AssignedBDR ?? '',
        CAM: parsedStats?.AssignedCAM ?? '',
        CSM: parsedStats?.AssignedCSM ?? '',
        SE: parsedStats?.AssignedSE ?? '',
        SUR: parsedStats?.AssignedSetupRep ?? '',
      };
    } else {
      return {
        businessId: '0',
        name: 'no name',
        timezone: 'no timezone',
        activeAgents: '0',
        numbers: '0',
        unallocatedNumbers: '0',
        blockedNumbers: '0',
        ringGroups: '0',
        teams: '0',
        queues: '0',
        ivrs: '0',
        conferences: '0',
        dispositions: '0',
        cadences: '0',
        powerlists: '0',
        webhooks: '0',
        contacts: '0',
        crmStats: [] as ICRMStats[],
        apiUsedRequests: '0',
        AE: '',
        BDR: '',
        CAM: '',
        CSM: '',
        SE: '',
        SUR: '',
      };
    }
  }, [superAdminBusinessStatsData]);

  const crmNames = {
    0: 'None',
    4: 'Salesforce',
    13: 'Zoho',
    16: 'Hubspot',
    17: 'Pipedrive',
  } as const;

  const getCRMName = (crmid: number) => {
    return crmNames[crmid] ?? `CRM ID ${crmid}`;
  };

  const { show } = useSelector((state) => state.superAdminStats);
  const dispatch = useDispatch();
  const toggleClickHandler = () => {
    const element = document.getElementsByTagName('body')?.[0];
    document.documentElement.style.setProperty('--AdminStatsHeight', '0px');
    if (show) {
      dispatch(toggleSuperAdminDrawer(false));
      if (element) {
        element.classList.remove('bid-stats-open');
      }
    } else {
      refetchToggleForSuperAdminBusinessStats();
      dispatch(toggleSuperAdminDrawer(true));
      if (element) {
        document.body.classList.add('bid-stats-open');

        const adminStats = document.getElementById('bidStatsContainer');
        const adminStatsHeight = adminStats?.offsetHeight ?? 97;
        document.documentElement.style.setProperty(
          '--AdminStatsHeight',
          `${adminStatsHeight}px`
        );
        element.classList.add('bid-stats-open');
      }
    }
  };

  useEffect(() => {
    if (
      superAdminBusinessStatsSuccess &&
      userAtom.contents &&
      'user' in userAtom.contents
    ) {
      if (show === true) {
        const loginUser = userAtom.contents.user ?? {};
        if (loginUser.role === 'superadmin') {
          document.body.classList.add('bid-stats-open');
          const adminStats = document.getElementById('bidStatsContainer');
          const adminStatsHeight = adminStats?.offsetHeight ?? 97;
          document.documentElement.style.setProperty(
            '--AdminStatsHeight',
            `${adminStatsHeight}px`
          );
        }
      }
    }
  }, [superAdminBusinessStatsData]);

  // NEW_EFFECT_CLEANUP_START
  // Clean up when the component unmounts to avoid leaving the page shifted
  useEffect(() => {
    return () => {
      // Ensure the drawer state is reset
      dispatch(toggleSuperAdminDrawer(false));

      // Remove any styles or classes that were added while the drawer was mounted
      const bodyEl = document.body;
      if (bodyEl) {
        bodyEl.classList.remove('bid-stats-open');
      }
      document.documentElement.style.setProperty('--AdminStatsHeight', '0px');
    };
  }, [dispatch]);
  // NEW_EFFECT_CLEANUP_END

  const isDeveloper = useMemo(() => {
    return userAtom.contents && 'user' in userAtom.contents
      ? getIsDeveloper(getUserSettings(userAtom.contents))
      : false;
  }, [userAtom]);

  return (
    <Dropdown
      drop={'down'}
      show={show}
      className={`statsContainer ${classes.statsContainer}`}
      id="bidStatsContainer"
      // autoClose={false}
      onSelect={(_eventKey, event) => event?.preventDefault()}
    >
      {/* <div className="separator separator-dashed my-7"></div> */}
      {/* <h6>Internal BID STATS</h6> */}

      {superAdminBusinessStatsLoading ? (
        <div className={classes.noDataContainer}>
          <Skeleton className={classes.barSkeleton} variant="rectangular" />
        </div>
      ) : (
        <div>
          <Dropdown.Menu
            className={`${classes.statsInner}`}
            show={show}
            // autoClose={false}
          >
            <div className={`${classes.statsFlexContainer}`}>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <Tooltip
                  title={`${
                    adminBusinessStats.prepaidTrial ? 'On Trial' : 'Paid'
                  }`}
                >
                  <span className={`${classes.adminBolded}`}>
                    <NavLink
                      to="/manage/account"
                      style={{
                        color: `${
                          adminBusinessStats.prepaidTrial ? 'red' : 'green'
                        }`,
                      }}
                    >
                      {adminBusinessStats?.name?.substring(0, 15)}
                      {adminBusinessStats?.name?.length > 15 && '...'}
                    </NavLink>
                  </span>
                </Tooltip>
                ({adminBusinessStats?.businessId}) <br />
                <Tooltip title={'Paid Months'}>
                  <span>Months: {adminBusinessStats?.PaidMonthsCt}</span>
                </Tooltip>{' '}
                <br />
                <Tooltip title={'Avg MRR'}>
                  <span>{adminBusinessStats?.AvgMRR}</span>
                </Tooltip>{' '}
                /{' '}
                <Tooltip title={'Max MRR'}>
                  <span>{adminBusinessStats?.MaxMRR}</span>
                </Tooltip>{' '}
                <br />
                <Tooltip title={'Avg MC'}>
                  <span>{adminBusinessStats?.AvgChange}</span>
                </Tooltip>{' '}
                /{' '}
                <Tooltip title={'Max MC'}>
                  <span>{adminBusinessStats?.MaxChange}</span>
                </Tooltip>{' '}
                <br />
                {adminBusinessStats?.timezone} <br />
                <Tooltip title={'Revenue per Active User'}>
                  <span>{adminBusinessStats?.revPerActiveUser}</span>
                </Tooltip>{' '}
                <br />
                <Tooltip title={'Onboarding Walkthrough Status'}>
                  <span>{adminBusinessStats?.OBWStatus}</span>
                </Tooltip>
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <Tooltip title={'Active Agents'}>
                  <span className={`${classes.adminBolded}`}>
                    <NavLink
                      to={{
                        pathname: '/manage/agents',
                        search: location.search,
                      }}
                    >
                      Seats{' '}
                    </NavLink>
                    ({adminBusinessStats?.activeAgents})
                  </span>
                </Tooltip>
                <br />
                Free: {adminBusinessStats?.payperuse} <br />
                Basic: {adminBusinessStats?.basicphone} (
                <Tooltip title={'Unlimited'}>
                  <span>{adminBusinessStats?.basicphoneunlimited}</span>
                </Tooltip>
                ) <br />
                Int: {adminBusinessStats?.integratedphone} (
                <Tooltip title={'Unlimited'}>
                  <span>{adminBusinessStats?.integratedphoneunlimited}</span>
                </Tooltip>
                ) <br />
                Prof: {adminBusinessStats?.enterprisephone} (
                <Tooltip title={'Unlimited'}>
                  <span>{adminBusinessStats?.enterprisephoneunlimited}</span>
                </Tooltip>
                ) <br />
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <span className={`${classes.adminBolded}`}>
                  <NavLink
                    to={{
                      pathname: '/manage/agents',
                      search: location.search,
                    }}
                  >
                    Addons
                  </NavLink>
                </span>
                <br />
                <Tooltip title={'Connection Boost Lite'}>
                  <span>CB: {adminBusinessStats?.localpresencelite}</span>
                </Tooltip>{' '}
                <Tooltip title={'Connection Boost Premium'}>
                  <span>CBP: {adminBusinessStats?.connectionboostprivate}</span>
                </Tooltip>
                <br />
                <Tooltip title={'DNC Blocker'}>
                  <span>DNC: {adminBusinessStats?.ftcblocking}</span>
                </Tooltip>{' '}
                <br />
                <Tooltip title={'Conversation Intelligence'}>
                  <span>CI: {adminBusinessStats?.callTranscription}</span>
                </Tooltip>{' '}
                <br />
                <Tooltip title={'AI Human Detect PowerList'}>
                  <span>MD: {adminBusinessStats?.machineDetection}</span>
                </Tooltip>{' '}
                <br />
                PL: (
                <Tooltip title={'PowerList 1'}>
                  <span>{adminBusinessStats?.powerdialer1}</span>
                </Tooltip>
                ) (
                <Tooltip title={'PowerList 4'}>
                  <span>{adminBusinessStats?.powerdialer4}</span>
                </Tooltip>
                ) (
                <Tooltip title={'PowerList 10'}>
                  <span>{adminBusinessStats?.powerdialer10}</span>
                </Tooltip>
                ) <br />
              </div>

              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <span className={`${classes.adminBolded}`}>
                  <NavLink
                    to={{
                      pathname: '/billing/subscription',
                    }}
                  >
                    Billing{' '}
                  </NavLink>
                  ({adminBusinessStats?.EndingMRR}){' '}
                  <a
                    href={`https://dashboard.stripe.com/customers/${adminBusinessStats?.StripeCustomerID}`}
                    target={'_blank'}
                  >
                    <Tooltip title={'Stripe'}>
                      <CreditCardOutlinedIcon />
                    </Tooltip>
                  </a>
                </span>
                <br />
                Day: {adminBusinessStats?.billday}
                <Tooltip title={'Billing Period'}>
                  <span> Cycle: {adminBusinessStats?.businessBillingTerm}</span>
                </Tooltip>
                <Tooltip title={'Contract Terms'}>
                  <span> Con: {adminBusinessStats?.businessContractTerm}</span>
                </Tooltip>{' '}
                <br />
                {adminBusinessStats?.lastinvoicedate != 0 && (
                  <>
                    Last Invoice: {adminBusinessStats?.lastinvoicedate} <br />
                  </>
                )}
                {adminBusinessStats?.invoicedUntil != 0 && (
                  <>
                    Inv. Until:{' '}
                    {DateTime.fromISO(
                      adminBusinessStats?.invoicedUntil
                    ).toLocaleString(DateTime.DATE_SHORT)}{' '}
                    <br />
                  </>
                )}
                <Tooltip title={'Reserve'}>
                  <span>{adminBusinessStats?.reserveBalance} </span>
                </Tooltip>{' '}
                (
                <Tooltip title={'Minimum Balance'}>
                  <span>{adminBusinessStats?.minimumBalance} </span>
                </Tooltip>{' '}
                /
                <Tooltip title={'Recharge Amount'}>
                  <span>{adminBusinessStats?.rechargeAmount} </span>
                </Tooltip>{' '}
                ) <br />
                Discount: {adminBusinessStats?.discountPercent}% <br />
                <Tooltip title={'4 day Credit Count'}>
                  <span>{adminBusinessStats?.fourDayRechargeCount} </span>
                </Tooltip>{' '}
                (
                <Tooltip title={'4 day Credit Amount'}>
                  <span>{adminBusinessStats?.fourDayRechargeAmount} </span>
                </Tooltip>
                )
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <span className={`${classes.adminBolded}`}>System</span> <br />
                <NavLink to="/manage/ring-groups">Groups: </NavLink>
                {adminBusinessStats?.ringGroups} <br />
                <NavLink to="/manage/ivrs">IVRs: </NavLink>
                {adminBusinessStats?.ivrs} <br />
                <NavLink to="/manage/queues">Queues: </NavLink>
                {adminBusinessStats?.queues} <br />
                <NavLink to="/manage/teams">Teams: </NavLink>
                {adminBusinessStats?.teams} <br />
                <NavLink to="/manage/numbers/business-sms">TeamSMS: </NavLink>
                {adminBusinessStats?.businessSMScount} <br />
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <span className={`${classes.adminBolded}`}>Automations</span>
                <br />
                <NavLink to="/manage/powerlists">PowerLists: </NavLink>
                {adminBusinessStats?.powerlists} <br />
                <NavLink to="/manage/cadences">Cadences: </NavLink>
                {adminBusinessStats?.cadences} <br />
                <NavLink to="/manage/webhooks">Webhooks: </NavLink>
                {adminBusinessStats?.webhooks} <br />
                SMSt:{adminBusinessStats?.smstcount} <br />
                VMD:{adminBusinessStats?.vmdcount} <br />
                <NavLink to="/manage/dispositions">
                  <Tooltip title={'Call Outcomes'}>
                    <span>Disp:</span>
                  </Tooltip>{' '}
                </NavLink>{' '}
                {adminBusinessStats?.dispositionCount}{' '}
                <Tooltip title={'Call Outcomes From CRM'}>
                  <span>E: {adminBusinessStats?.externalDispositionCount}</span>
                </Tooltip>{' '}
                <Tooltip title={'Call Outcomes With Rules'}>
                  <span>R: {adminBusinessStats?.dispositionRulesCount}</span>
                </Tooltip>{' '}
                <br />
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <span className={`${classes.adminBolded}`}>
                  <NavLink to="/manage/numbers">Numbers</NavLink> (
                  {adminBusinessStats?.numbers})
                </span>
                <br />
                <NavLink to="/manage/numbers/blocked">Blocked: </NavLink>
                {adminBusinessStats?.blockedNumbers} <br />
                <Tooltip title={'Unallocated'}>
                  <span>Unalloc</span>
                </Tooltip>{' '}
                : {adminBusinessStats?.unallocatedNumbers} <br />
                Spam:
                {adminBusinessStats?.ImageSpamCount} (
                {adminBusinessStats?.ImageSpamPct}%) <br />
                NonSMS: {adminBusinessStats?.NonSMS} (
                {adminBusinessStats?.NonSMSPct}%)
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <Tooltip title={'Per Agent averages over the last 7 days'}>
                  <span className={`${classes.adminBolded}`}>Call Stats</span>
                </Tooltip>
                <br />
                <Tooltip title={'Outgoing Calls / Agent'}>
                  <span>{adminBusinessStats.agentStatAvgOutgoingPerAgent}</span>
                </Tooltip>{' '}
                (
                <Tooltip title={'Outgoing Answered Calls / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgOutgoingAnsweredPerAgent}
                  </span>
                </Tooltip>
                )
                <br />
                <Tooltip title={'Outgoing Calls with Duration > 2.5 min.'}>
                  <span>
                    {
                      adminBusinessStats.agentStatAvgOutgoingCountOver150PerAgent
                    }
                  </span>
                </Tooltip>{' '}
                (
                <Tooltip
                  title={'Percent of Outgoing Calls with Duration > 2.5 min.'}
                >
                  <span>
                    {
                      adminBusinessStats.agentStatOutgoingConnectionRateOver150PerAgent
                    }
                    %
                  </span>
                </Tooltip>
                )<br />
                <Tooltip title={'Outgoing Calls with Duration < 30 sec.'}>
                  <span>
                    {
                      adminBusinessStats.agentStatOutgoingConnectionUnder30PerAgent
                    }
                  </span>
                </Tooltip>
                <br />
                <Tooltip title={'Incoming Calls / Agent'}>
                  <span>{adminBusinessStats.agentStatAvgIncomingPerAgent}</span>
                </Tooltip>{' '}
                (
                <Tooltip title={'Percent of Incoming Calls that are Missed'}>
                  <span>
                    {adminBusinessStats.agentStatIncomingMissedRatePerAgent}%
                  </span>
                </Tooltip>
                )
              </div>
              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <Tooltip title={'Per Agent averages over the last 7 days'}>
                  <span className={`${classes.adminBolded}`}>SMS Stats</span>
                </Tooltip>
                <br />
                <Tooltip title={'Outgoing SMS / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgOutgoingPerAgentSMS}
                  </span>
                </Tooltip>{' '}
                (
                <Tooltip title={'Outgoing MMS / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgOutgoingMMSPerAgentSMS}
                  </span>
                </Tooltip>
                ) (
                <Tooltip title={'Outgoing Blocked SMS / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgOutgoingBlockedPerAgentSMS}
                  </span>
                </Tooltip>
                )
                <br />
                <Tooltip title={'Incoming SMS / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgIncomingPerAgentSMS}
                  </span>
                </Tooltip>{' '}
                (
                <Tooltip title={'Incoming MMS / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgIncomingMMSPerAgentSMS}
                  </span>
                </Tooltip>
                ) (
                <Tooltip title={'Incoming Blocked SMS / Agent'}>
                  <span>
                    {adminBusinessStats.agentStatAvgIncomingBlockedPerAgentSMS}
                  </span>
                </Tooltip>
                )
                <br />
              </div>
              {adminBusinessStats.crmStats.length > 0 &&
                adminBusinessStats.crmStats.map((stat, i) => {
                  const crmName = getCRMName(stat.crmid);
                  return (
                    <div
                      key={`admin-stats-${i}`}
                      className={`font-size-sm px-2 ${classes.statItemContainer}`}
                    >
                      <NavLink to="/manage/contacts">
                        <span className={`${classes.adminBolded}`}>
                          {crmName}
                        </span>
                      </NavLink>
                      <br />
                      <Fragment key={`crmStat-${stat.crmid}-labels`}>
                        <span>Users: {stat.usersCount}</span>
                        <br />
                        <span>Contacts: {stat.contactsCount}</span>
                        {isDeveloper &&
                          typeof stat.esContactsCount === 'number' && (
                            <>
                              <br />
                              <span>
                                ES:{' '}
                                <span
                                  className={
                                    stat.esContactsCount >= stat.contactsCount
                                      ? classes.statGreaterOrEqual
                                      : classes.statLesser
                                  }
                                >
                                  {stat.esContactsCount}
                                </span>
                              </span>
                            </>
                          )}
                      </Fragment>
                    </div>
                  );
                })}

              <div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <span className={`${classes.adminBolded}`}>
                  <NavLink to="/internal/api-usage">API</NavLink>
                </span>
                <PopoverGeneric
                  type="IconButton"
                  iconForButton={<BarChartIcon />}
                  showPopover={showUsageReport}
                  showPopoverIcon={false}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  popoverData={
                    <div>
                      <HighchartsReactWithStyles
                        allowChartUpdate={true}
                        highcharts={Highcharts}
                        options={usageReportChartOptions}
                      />
                    </div>
                  }
                />
                <br />
                {adminBusinessStats?.apiUsedRequests}
              </div>
              {adminBusinessStats?.BDR ||
              adminBusinessStats?.AE ||
              adminBusinessStats?.SE ||
              adminBusinessStats?.CAM ||
              adminBusinessStats?.CSM ||
              adminBusinessStats?.SUR ? (
                <div
                  className={`font-size-sm px-2 ${classes.statItemContainer}`}
                >
                  <span className={`${classes.adminBolded}`}>Internal</span>
                  <br />
                  {adminBusinessStats?.BDR !== '' && (
                    <>
                      BDR: {adminBusinessStats?.BDR.replace('@kixie.com', '')}
                      <br />
                    </>
                  )}
                  {adminBusinessStats?.AE !== '' && (
                    <>
                      AE: {adminBusinessStats?.AE.replace('@kixie.com', '')}
                      <br />
                    </>
                  )}
                  {adminBusinessStats?.SE !== '' && (
                    <>
                      SE: {adminBusinessStats?.SE.replace('@kixie.com', '')}
                      <br />
                    </>
                  )}
                  {adminBusinessStats?.CAM !== '' && (
                    <>
                      CAM: {adminBusinessStats?.CAM.replace('@kixie.com', '')}
                      <br />
                    </>
                  )}
                  {adminBusinessStats?.CSM !== '' && (
                    <>
                      CSM: {adminBusinessStats?.CSM.replace('@kixie.com', '')}
                      <br />
                    </>
                  )}
                  {adminBusinessStats?.SUR !== '' && (
                    <>
                      SUR: {adminBusinessStats?.SUR.replace('@kixie.com', '')}
                      <br />
                    </>
                  )}
                </div>
              ) : null}
            </div>
            {/*todo: put the notes in the header, probably only display Top note and expand via Popover for the rest*/
            /*<div className={`font-size-sm px-2 ${classes.statItemContainer}`}>
                <Notes />
            </div>*/}
          </Dropdown.Menu>
          <Dropdown.Toggle
            onClick={toggleClickHandler}
            className={classes.bidStatsToggle}
            id="bid-stats-toggle"
            data-toggle="dropdown"
          >
            {/* <span className={`svg-icon svg-icon-xl ${classes.tabIcon}`}>
              <SVG
                src={toAbsoluteUrl(
                  '/media/svg/icons/Navigation/Angle-double-down.svg'
                )}
              />
            </span> */}
            <i className={`fa fa-chevron-down ${classes.bidStatsToggleIcon}`} />
          </Dropdown.Toggle>
        </div>
      )}
    </Dropdown>
  );
};

export default SuperAdminStats;
